import { sql } from 'drizzle-orm'
import {
  type AnyPgColumn,
  check,
  index,
  integer,
  pgEnum,
  pgTable,
  smallint,
  text,
  timestamp,
  uniqueIndex,
} from 'drizzle-orm/pg-core'

import { user } from './auth.ts'
import { createdAt, primaryId, updatedAt } from './columns.ts'
import { city } from './geo.ts'
import { offering, offeringVariant } from './offerings.ts'
import { order } from './orders.ts'

/**
 * The basket, before it is an order.
 *
 * Three values, and `ABANDONED` is deliberately NOT one of them. Abandonment is
 * a QUESTION asked of a row — "still ACTIVE, has a complete line, and nothing
 * happened for two hours" — not a state somebody has to remember to write. A
 * status would need a scheduler to maintain it, a scheduler on Vercel needs
 * `vercel.json`, and this repo deliberately has none (see AGENTS.md, _Git &
 * deploy_). A predicate needs nothing and cannot go stale.
 *
 * `PURGED` is the tombstone: the lines are gone and the row is anonymous, but
 * the counters survive so a time series longer than the data retention is still
 * possible. Same shape as `deleted-<id>@flou.invalid` in `closeUserAccount`.
 */
export const cartStatus = pgEnum('cart_status', ['ACTIVE', 'CONVERTED', 'PURGED'])

/**
 * How long a basket lives, as ONE number.
 *
 * It sets the row's `expires_at` and the cookie's `Max-Age`, and it has to be a
 * single constant because those two are the same promise told to two different
 * parties. A cookie that outlives its row leaves somebody staring at an empty
 * basket they were told they still had; a row that outlives its cookie is
 * personal data nobody can reach to delete.
 *
 * Thirty days. Long enough that a weekend shoot planned on Monday is still there
 * on Friday, short enough to be a defensible retention period rather than "we
 * keep it".
 */
export const CART_TTL_MS = 30 * 24 * 60 * 60 * 1000

/**
 * One visitor's basket, addressed by an opaque token in a cookie.
 *
 * **The cookie carries `token`, never `id`.** Same reasoning as `session.token`:
 * the primary key is what every foreign key and every log line quotes, and a
 * value the browser hands back on each request should not be the value the rest
 * of the schema points at. A leaked token costs one basket; a leaked primary key
 * is an oracle for how many rows exist.
 *
 * **The row is created LAZILY — never by a page view.** Opening `/pl/kreator`
 * writes nothing at all; the first row and the first cookie appear on the first
 * MUTATION. That is what keeps a crawler, a link preview and a visitor who
 * changed their mind from each producing a personal-data record, and it is the
 * single strongest data-minimisation property of this design. Anything that
 * would create a row on read is a bug, not an optimisation.
 *
 * **There are no contact columns here, on purpose.** A basket is an intention,
 * not a correspondence: e-mail recovery of an abandoned basket is explicitly out
 * of scope. If an address is ever added it arrives WITH a `privacy_consent_at`
 * beside it and a CHECK that neither exists alone — the rigour `lead` already
 * keeps. Until then the only identifier is the cookie, which is why
 * `discardCart` exists: it is the only erasure route a person with no account
 * and no address actually has.
 *
 * **There are no money columns here either**, and that is the sharpest contrast
 * with `order`. An order freezes what somebody agreed to pay. A basket is priced
 * on READ, from the live catalogue, every time — so a price change is visible
 * before checkout rather than after it, and a basket can never quote one number
 * and bill another.
 */
export const cart = pgTable(
  'cart',
  {
    id: primaryId('cart'),
    /** What the cookie carries. Opaque, unguessable, never rendered. */
    token: text('token').notNull().unique(),
    /**
     * Set once a signed-in person touches the basket. NULL for a guest — guest
     * checkout is a supported path, so an anonymous basket is a first-class row
     * and not a half-finished one.
     */
    userId: text('user_id').references(() => user.id, { onDelete: 'set null' }),

    status: cartStatus('status').notNull().default('ACTIVE'),
    /** The order this basket became. Set in the SAME transaction as the insert. */
    convertedOrderId: text('converted_order_id').references(() => order.id, {
      onDelete: 'set null',
    }),

    /**
     * The clock the abandonment predicate reads. Distinct from `updatedAt`
     * because they answer different questions: `updatedAt` is "when did this row
     * change", which a retention sweep or a purge also moves; this is "when did a
     * PERSON last do something", which is the only thing abandonment means.
     *
     * Stamped exclusively through the `touchCart` helper. Nothing in this schema
     * uses `$onUpdate` — every writer stamps by hand — so a second writer with
     * its own `.set()` is how this column silently stops being true and takes the
     * metric with it.
     */
    lastActivityAt: timestamp('last_activity_at', { withTimezone: true }).notNull().defaultNow(),
    /**
     * Retention, written down rather than implied. Fed by the same `CART_TTL_MS`
     * constant that sets the cookie's `Max-Age`, so the row and the cookie cannot
     * promise different lifetimes.
     */
    expiresAt: timestamp('expires_at', { withTimezone: true }).notNull(),
    purgedAt: timestamp('purged_at', { withTimezone: true }),

    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => [
    // The abandonment predicate, as an index: status first (it is an equality),
    // then the clock it ranges over.
    index('cart_status_activity_idx').on(t.status, t.lastActivityAt),
    index('cart_user_idx').on(t.userId),
    // The retention sweep is `WHERE expires_at < now()` over the whole table.
    index('cart_expires_idx').on(t.expiresAt),

    // One-directional on purpose. "CONVERTED implies an order" is what we want
    // to say, but `onDelete: 'set null'` may blank the reference later, and a
    // biconditional CHECK would then refuse the delete. This says the half that
    // stays true forever: an order reference only ever belongs to a converted
    // basket.
    check(
      'cart_converted_order_only_when_converted',
      sql`${t.convertedOrderId} IS NULL OR ${t.status} = 'CONVERTED'`,
    ),
    check('cart_purged_at_only_when_purged', sql`${t.purgedAt} IS NULL OR ${t.status} = 'PURGED'`),
  ],
)

/**
 * One configured position. Stores the PICK, never the resolved reference.
 *
 * This is the single most consequential decision in the table, so it is written
 * down rather than left to be rediscovered. `toCartRef` in the app is a LOSSY
 * function: it folds the start time into `from` as `YYYY-MM-DDTHH:MM`, and for a
 * per-day offering it derives `to` from the quantity. Persisting its OUTPUT
 * would mean a row that can be checked out but not EDITED — reopening a basket
 * could not repopulate the form it came from. That is exactly the "stratna
 * rehydracja" that made an earlier attempt at merging the basket and the creator
 * back off.
 *
 * So the flow is one-directional and enforced by what is stored:
 * `pick → toCartRef → ref → lineKey`. Never the reverse.
 *
 * Days are `YYYY-MM-DD` TEXT and the start time is `HH:MM` TEXT, matching
 * `assistant_thread.day` and the app's rule that a `Date` never travels. A
 * `date` column would hand back a `Date` at the driver boundary and put the
 * conversion back in play on every read.
 */
export const cartItem = pgTable(
  'cart_item',
  {
    id: primaryId('citem'),
    cartId: text('cart_id')
      .notNull()
      .references(() => cart.id, { onDelete: 'cascade' }),
    /**
     * `cascade`, unlike `booking`'s `restrict`. A reservation is an agreement and
     * must not vanish under the catalogue; a basket line is disposable, and
     * blocking an admin from deleting an offering because somebody has it in a
     * basket would be absurd. Deactivation — the normal case — is not a delete
     * and shows up as an `orphan` line on the screen instead.
     */
    offeringId: text('offering_id')
      .notNull()
      .references(() => offering.id, { onDelete: 'cascade' }),
    /** NULL means "not configured yet" — the line is a draft, not broken. */
    offeringVariantId: text('offering_variant_id').references(() => offeringVariant.id, {
      onDelete: 'set null',
    }),
    cityId: text('city_id').references(() => city.id, { onDelete: 'set null' }),

    quantity: integer('quantity').notNull().default(1),
    /** `YYYY-MM-DD`. Start day, or the only day. */
    fromDay: text('from_day'),
    /** `YYYY-MM-DD`. Inclusive end — a one-day hire has `toDay === fromDay`. */
    toDay: text('to_day'),
    /** `HH:MM`. Only a room booked by the hour has one. */
    startTime: text('start_time'),

    /**
     * What the customer wants made (spec §6 · G1). Carried by the BASKET so it
     * survives a sign-in, a device change and thirty days of thinking about it
     * — the same reason every other configuration field is here rather than in
     * the browser.
     *
     * Deliberately NOT part of `lineKey`. The signature answers "is this the
     * same line as that one?", and a brief is a description of the line rather
     * than part of its identity: two thumbnails of the same level ARE the same
     * basket line, and spec §6 refines each piece in its own chat once the work
     * starts. Including it would also mean a deep link — which carries no brief
     * — could never merge into a line the customer had already described.
     */
    brief: text('brief'),
    /**
     * Where the raw material is (spec §7 · G4). Like `brief` above and for the
     * same reason, deliberately NOT part of `line_key`: two reels edited from
     * the same footage are still two lines, and one reel whose link the client
     * corrects is still the same line.
     */
    sourceUrl: text('source_url'),
    /**
     * Which length band a film line sits on (spec §8 · G5) — the slider's
     * position, 0-based. NULL for everything that is not priced by the minute.
     *
     * The INDEX rather than the two bounds, because the bands are a rule and
     * not data: storing 20 and 30 in a basket that sits for thirty days would
     * let a re-priced rate card meet a stale ceiling. Part of `line_key`
     * through `quantity`, which the band determines.
     */
    lengthBandIndex: smallint('length_band_index'),
    /**
     * Minutes of raw footage the client declared (spec §8 · G7). NULL for
     * anything that needs no source material.
     *
     * Not part of `line_key`: two reels cut from different amounts of tape are
     * still two reels, and a client correcting their estimate is still editing
     * the same line.
     */
    footageMinutes: smallint('footage_minutes'),

    // ── WHAT A SHOOT DAY ACTUALLY IS (the owner's 2026 model) ─────────────
    // Until these three, a photography line carried a plan and nothing else,
    // and the basket priced it at the plan's day rate: 400 zł for a day that
    // the site had just told the customer costs 2 450 zł. The site knew, the
    // basket did not, and the gap between them was the whole configuration.
    //
    // They are a TRIPLE and are only ever written together, because a price
    // needs all three — the session says which rate card, the scope says which
    // row of it, and the photo count is the number that moves along the row.
    // See `shoot-price.ts`.

    /** Session slug: `wizerunkowa`, `rodzinna`, `nieruchomosci`, … */
    shootSession: text('shoot_session'),
    /**
     * Which row of that session's table — a head count (`'5'`), a product
     * count, or an area band (`'101-150'`).
     */
    shootScope: text('shoot_scope'),
    /**
     * How many finished photos, from the three that row offers.
     *
     * The NUMBER and not an index, because it is a promise to the customer
     * („dostaniesz 30 zdjęć") rather than a position in a list. A re-priced
     * table would move an index under a basket that had sat for a month; it
     * cannot move a number somebody was told.
     */
    shootPhotos: smallint('shoot_photos'),

    /**
     * WHAT IS BEING RECORDED — the operator's half of the same question.
     *
     * A `recording_type.slug`: `youtube`, `podcast`, `rolki`, … It is the
     * operator's equivalent of {@link cartItem.shootSession} and deliberately
     * NOT that column: a photographer's session comes with a scope and a photo
     * count and is checked as a triple, while a recording day is one answer on
     * its own. Folding them together would have meant relaxing that check for
     * every line in the basket.
     *
     * NULL on everything that is not an operator's day — including a
     * photographer's, which says what it is in the three columns above.
     *
     * A text slug and not a foreign key, for the reason `shootSession` is one:
     * the basket stores the ANSWER, and a kind that is later turned off must
     * not take a live basket line down with it.
     */
    recordingType: text('recording_type'),

    /**
     * "Te rolki mają powstać z tego filmu" (spec §8 · G8).
     *
     * Points at ANOTHER LINE OF THE SAME BASKET — the film this one is to be
     * cut from. It is what turns two independent purchases into one job with an
     * order to it, and it is the basket's copy of `order_item.depends_on_item_id`,
     * which is where the consequences are written down at length.
     *
     * Deliberately NOT part of `line_key`, like `brief` and `source_url` above:
     * two reels cut from the same film are still two lines, and ticking the box
     * on a line already in the basket must not mint a second one beside it.
     *
     * `SET NULL` on the same terms the order column uses: removing the film
     * from the basket leaves the reels standing, needing their own tape — which
     * `pickProblem` then says out loud, rather than silently ordering a reel
     * made of nothing.
     */
    dependsOnItemId: text('depends_on_item_id').references((): AnyPgColumn => cartItem.id, {
      onDelete: 'set null',
    }),

    /**
     * Content SIGNATURE, not identity. `id` is the identity: it is minted once,
     * survives every edit, and is what React keys on, what an action addresses
     * and what focus returns to. This changes whenever the configuration does,
     * and it exists for exactly two jobs — spotting a duplicate, and letting a
     * deep link aim at a line that already exists.
     *
     * NULL while the line is incomplete. Postgres treats NULLs in a unique index
     * as distinct, so any number of drafts coexist while two COMPLETE lines with
     * the same configuration collide — which turns de-duplication into a database
     * invariant instead of a race between two browser tabs.
     */
    lineKey: text('line_key'),
    sortOrder: integer('sort_order').notNull().default(0),

    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => [
    index('cart_item_cart_idx').on(t.cartId, t.sortOrder),
    index('cart_item_offering_idx').on(t.offeringId),
    uniqueIndex('cart_item_line_key_unique').on(t.cartId, t.lineKey),

    check('cart_item_quantity_positive', sql`${t.quantity} > 0`),
    // All three or none. Two of them cannot be priced and are not a partial
    // answer — they are a line that says it is a photography shoot and then
    // cannot say what of. The basket would fall back to the plan's day rate
    // and quietly undercharge, which is the exact bug these columns exist to
    // close.
    check(
      'cart_item_shoot_triple',
      sql`(${t.shootSession} IS NULL) = (${t.shootScope} IS NULL)
          AND (${t.shootSession} IS NULL) = (${t.shootPhotos} IS NULL)`,
    ),
    check('cart_item_shoot_photos_positive', sql`${t.shootPhotos} IS NULL OR ${t.shootPhotos} > 0`),
    // An end without a start is nonsense; a start without an end is a single
    // day, which is the normal shape for everything booked by the day.
    check('cart_item_period_paired', sql`${t.toDay} IS NULL OR ${t.fromDay} IS NOT NULL`),
    // `>=`, NOT `>`. These are INCLUSIVE days, unlike `booking`'s half-open
    // timestamps: a one-day hire is `to = from`, and a strict comparison would
    // reject the most common rental there is.
    check(
      'cart_item_period_ordered',
      sql`${t.toDay} IS NULL OR ${t.fromDay} IS NULL OR ${t.toDay} >= ${t.fromDay}`,
    ),
  ],
)

export type Cart = typeof cart.$inferSelect
export type NewCart = typeof cart.$inferInsert
export type CartItem = typeof cartItem.$inferSelect
export type NewCartItem = typeof cartItem.$inferInsert
